import { ActionTree } from 'vuex'

import api from '@/apiClient'

import { ItemGroup, ItemGroupResourcePayload } from '@/models'

import { assertToken, mergeById } from '@/utils'

import { RootState } from '../../state'

import {
  ADD_ITEM_GROUP_ITEMS,
  REMOVE_ITEM_GROUP_ITEMS
} from '../items/mutations'

import {
  ADD_ITEM_GROUP_OPTION_GROUPS,
  REMOVE_ITEM_GROUP_OPTION_GROUPS
} from '../optionGroups/mutations'

import { MenuState } from '../state'

import {
  ADD_ITEM_GROUP,
  INCREMENT_ITEM_GROUPS_QUERY_INDEX,
  LOADED_ALL_ITEM_GROUPS,
  REMOVE_ITEM_GROUP,
  RESET_ITEM_GROUPS_QUERY_INDEX,
  UPDATE_ITEM_GROUPS,
  UPDATE_ITEM_GROUPS_TOTAL
} from './mutations'

const actions: ActionTree<MenuState, RootState> = {
  /**
   * Adds a new ItemGroup and prepares its relational objects.
   *
   * @param itemGroup ItemGroup to be added.
   */
  addItemGroup ({ commit, state }, itemGroup: ItemGroup) {
    commit(UPDATE_ITEM_GROUPS_TOTAL, state.itemGroups.total + 1)

    commit(ADD_ITEM_GROUP_ITEMS, itemGroup.id)
    commit(ADD_ITEM_GROUP_OPTION_GROUPS, itemGroup.id)

    commit(ADD_ITEM_GROUP, itemGroup)
  },

  /**
   * Creates an ItemGroup.
   *
   * It rejects if the user is not logged in.
   *
   * @param payload Payload to create the ItemGroup.
   */
  async createItemGroup ({ dispatch, rootState, rootGetters }, payload: ItemGroupResourcePayload) {
    const businessUnit = rootState.businessUnit
    const providerId: number | null = rootGetters['auth/currentProvider']

    if (providerId === null) {
      throw new Error('User is not authenticated')
    }

    if (businessUnit === null) {
      throw new Error('BusinessUnit is not loaded')
    }

    const tokenV2 = assertToken(rootState.auth.tokenV2)

    const itemGroup = await api.menu.itemGroups.createItemGroup(tokenV2, providerId, businessUnit.id, payload)

    await dispatch('addItemGroup', itemGroup)

    return itemGroup
  },

  /**
   * Loads one ItemGroup.
   *
   * It rejects if the user is not logged in.
   *
   * @param id ItemGroup id to be loaded.
   */
  async loadItemGroup ({ commit, dispatch, getters, rootState, rootGetters, state }, id: number) {
    const businessUnit = rootState.businessUnit
    const providerId: number | null = rootGetters['auth/currentProvider']

    if (providerId === null) {
      throw new Error('User is not authenticated')
    }

    if (businessUnit === null) {
      throw new Error('BusinessUnit is not loaded')
    }

    const tokenV2 = assertToken(rootState.auth.tokenV2)

    const itemGroup = await api.menu.itemGroups.fetchItemGroup(tokenV2, providerId, businessUnit.id, id)

    if (getters.itemGroupById(id) === null) {
      await dispatch('addItemGroup', itemGroup)
    } else {
      const itemGroups = mergeById(state.itemGroups.elements, [itemGroup])
      commit(UPDATE_ITEM_GROUPS, itemGroups)
    }
  },

  /**
   * Loads a page of ItemGroups.
   *
   * It rejects if the user is not logged in.
   *
   * It prepares the relational objects for each loaded ItemGroup.
   *
   * When a previously loaded page changes, it desynchronizes and will load
   * an empty page after the last page, which will reset the query index.
   */
  async loadItemGroupPage ({ commit, rootState, rootGetters, state }) {
    const businessUnit = rootState.businessUnit
    const providerId: number | null = rootGetters['auth/currentProvider']

    if (providerId === null) {
      throw new Error('User is not authenticated')
    }

    if (businessUnit === null) {
      throw new Error('BusinessUnit is not loaded')
    }

    const tokenV2 = assertToken(rootState.auth.tokenV2)

    const page = await api.menu.itemGroups.fetchItemGroupPage(
      tokenV2,
      providerId,
      businessUnit.id,
      state.itemGroups.query
    )

    if (state.itemGroups.total !== page.total) {
      commit(UPDATE_ITEM_GROUPS_TOTAL, page.total)
    }

    if (page.elements.length > 0) {
      for (const itemGroup of page.elements) {
        if (state.itemGroups.items[itemGroup.id] === undefined) {
          commit(ADD_ITEM_GROUP_ITEMS, itemGroup.id)
        }

        if (state.itemGroups.optionGroups[itemGroup.id] === undefined) {
          commit(ADD_ITEM_GROUP_OPTION_GROUPS, itemGroup.id)
        }
      }

      const itemGroups = mergeById(state.itemGroups.elements, page.elements)

      commit(UPDATE_ITEM_GROUPS, itemGroups)

      if (itemGroups.length === page.total) {
        commit(LOADED_ALL_ITEM_GROUPS)
      }

      commit(INCREMENT_ITEM_GROUPS_QUERY_INDEX, page.elements.length)
    } else {
      commit(RESET_ITEM_GROUPS_QUERY_INDEX)

      if (page.total === 0 && state.itemGroups.elements.length > 0) {
        commit(UPDATE_ITEM_GROUPS, [])
      }
    }
  },

  /**
   * Updates an ItemGroup.
   *
   * It rejects if the user is not logged in.
   *
   * @param id ItemGroup id to be updated.
   * @param payload Payload to update it.
   */
  async updateItemGroup (
    { commit, rootState, rootGetters, state },
    { id, payload }: { id: number; payload: ItemGroupResourcePayload }
  ) {
    const businessUnit = rootState.businessUnit
    const providerId: number | null = rootGetters['auth/currentProvider']

    if (providerId === null) {
      throw new Error('User is not authenticated')
    }

    if (businessUnit === null) {
      throw new Error('BusinessUnit is not loaded')
    }

    const tokenV2 = assertToken(rootState.auth.tokenV2)

    const itemGroup = await api.menu.itemGroups.updateItemGroup(tokenV2, providerId, businessUnit.id, id, payload)

    const itemGroups = mergeById(state.itemGroups.elements, [itemGroup])

    commit(UPDATE_ITEM_GROUPS, itemGroups)
  },

  /**
   * Updates an ItemGroup image.
   *
   * It rejects if the user is not logged in.
   *
   * @param id ItemGroup id whose image will be updated.
   * @param imageFile File to be uploaded.
   */
  async updateItemGroupImage (
    { commit, rootState, rootGetters, state },
    { id, imageFile }: { id: number; imageFile: File }
  ) {
    const businessUnit = rootState.businessUnit
    const providerId: number | null = rootGetters['auth/currentProvider']

    if (providerId === null) {
      throw new Error('User is not authenticated')
    }

    if (businessUnit === null) {
      throw new Error('BusinessUnit is not loaded')
    }

    const tokenV2 = assertToken(rootState.auth.tokenV2)

    const formData = new FormData()

    formData.append('file', imageFile)

    const itemGroup = await api.menu.itemGroups.updateItemGroupImage(tokenV2, providerId, businessUnit.id, id, formData)

    const itemGroups = mergeById(state.itemGroups.elements, [itemGroup])
    commit(UPDATE_ITEM_GROUPS, itemGroups)
  },

  /**
   * Removes an ItemGroup and its relational objects.
   *
   * It rejects if the user is not logged in.
   *
   * @param id ItemGroup id to be removed.
   */
  async removeItemGroup ({ commit, rootState, rootGetters, state }, id: number) {
    const businessUnit = rootState.businessUnit
    const providerId: number | null = rootGetters['auth/currentProvider']

    if (providerId === null) {
      throw new Error('User is not authenticated')
    }

    if (businessUnit === null) {
      throw new Error('BusinessUnit is not loaded')
    }

    const tokenV2 = assertToken(rootState.auth.tokenV2)

    await api.menu.itemGroups.deleteItemGroup(tokenV2, providerId, businessUnit.id, id)

    commit(REMOVE_ITEM_GROUP, id)

    commit(REMOVE_ITEM_GROUP_ITEMS, id)
    commit(REMOVE_ITEM_GROUP_OPTION_GROUPS, id)

    commit(UPDATE_ITEM_GROUPS_TOTAL, state.itemGroups.total - 1)
  }
}

export default actions
